// Hành trình có tên bưu cục của một vận đơn (@shoptrack/best/journey — bản tra được lưu lại, gộp dần, không mất
// bước cũ). Dùng chung: khung "Hành trình" ở chi tiết đơn (GET /orders/:id/tracking) và đơn của đơn vị ở khu
// Quản trị NPP (admin-api GET /orders/shops/:shopId/:orderId/tracking) — hai nơi trả cùng một dạng dữ liệu.

import { RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { phoneDisplay } from '@/lib/format'
import { cn } from '@/lib/utils'

export interface LiveTraceStep {
  at: string | null
  place: string | null
  site: string | null
  text: string
  courier: { name: string; phone: string } | null
  scanType: string | null
}

export interface LiveTrackingResult {
  configured: boolean
  tracking: {
    found: boolean
    status: string | null
    statusCode: string | null
    isReturn: boolean
    steps: LiveTraceStep[]
    needPhone: boolean
    message: string | null
  } | null
  fetchedAt: string | null
  error: string | null
}

const SCAN_LABEL: Record<string, string> = {
  '01': 'Đã lấy hàng',
  '02': 'Rời bưu cục',
  '03': 'Đến bưu cục',
  '04': 'Đang giao hàng',
  '17': 'Đã giao hàng',
}

const isOrderedStep = (s: LiveTraceStep) => !s.scanType && /ordered/i.test(s.text)

function stepTitle(s: LiveTraceStep): string {
  if (s.scanType && SCAN_LABEL[s.scanType]) return SCAN_LABEL[s.scanType]!
  return isOrderedStep(s) ? 'Đã tạo vận đơn' : 'Cập nhật hành trình'
}

function stepTime(iso: string | null): string {
  const d = iso ? new Date(iso) : null
  if (!d || isNaN(d.getTime())) return '—'
  const p2 = (n: number) => String(n).padStart(2, '0')
  return `${p2(d.getHours())}:${p2(d.getMinutes())} ${p2(d.getDate())}/${p2(d.getMonth() + 1)}`
}

/** Các bước đã tra được (mới nhất trước) — rỗng khi chưa có hành trình. */
export function journeySteps(result: LiveTrackingResult | undefined): LiveTraceStep[] {
  const t = result?.tracking
  return t?.found ? t.steps ?? [] : []
}

/** Lời ghi BEST: tên bưu cục trong 【】 in đậm, bỏ khối bưu tá (đã hiện riêng), dấu phẩy → " · ". */
function RemarkText({ text }: { text: string }) {
  const parts = text.replace(/【[^【】]*\/\s*\+?\d[\d .-]{7,}】/g, '').split(/【([^【】]+)】/)
  return (
    <>
      {parts.map((p, i) =>
        i % 2 ? (
          <span key={i} className="font-medium text-ink"> {p}</span>
        ) : (
          <span key={i}>{(i === 0 ? p.replace(/^[\s,]+/, '') : p).replace(/\s*,\s*/g, ' · ')}</span>
        ),
      )}
    </>
  )
}

export function LiveJourney({
  result,
  loading,
  error,
  refreshing,
  onRefresh,
}: {
  result: LiveTrackingResult | undefined
  loading: boolean
  error: Error | null
  refreshing: boolean
  onRefresh: () => void
}) {
  const tracking = result?.tracking ?? null
  const steps = journeySteps(result)

  if (loading) return <div className="flex items-center justify-center py-8 text-muted">Đang tra cứu hành trình…</div>
  if (error) return <div className="py-4 text-center text-sm text-danger">Không thể tra cứu: {error.message}</div>
  if (steps.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-inkSoft">
        {result?.error
          ? `Chưa tra được hành trình: ${result.error}`
          : tracking?.needPhone
            ? 'Hãng yêu cầu xác thực số điện thoại, số của đơn không khớp.'
            : result?.configured === false
              ? 'Chưa bật tra cứu hành trình bưu cục.'
              : 'Hãng chưa có hành trình cho vận đơn này.'}
        {result?.configured !== false && (
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing}>
              <RefreshCw className={cn('size-3.5', refreshing && 'animate-spin')} /> Thử lại
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink">
            {/* Mã dịch vụ chưa có tên → "Mã 20": lấy tên bước mới nhất */}
            {tracking?.status && !/^Mã\s/.test(tracking.status) ? tracking.status : stepTitle(steps[0]!)}
            {tracking?.isReturn && <span className="ml-1.5 rounded bg-warn/15 px-1.5 py-0.5 text-xs font-medium text-warn">Hoàn</span>}
          </p>
          {result?.fetchedAt && <p className="text-xs text-muted">Cập nhật {stepTime(result.fetchedAt)}</p>}
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing} aria-label="Tra lại hành trình">
          <RefreshCw className={cn('size-3.5', refreshing && 'animate-spin')} /> Tra lại
        </Button>
      </div>
      {result?.error && (
        <p className="mb-3 rounded-lg bg-warn/10 px-3 py-2 text-xs text-warn">
          Chưa tra được hành trình mới ({result.error}) — đang hiện bản đã lưu.
        </p>
      )}
      <ol className="relative space-y-3 before:absolute before:bottom-2 before:left-[7px] before:top-2 before:w-px before:bg-rule">
        {steps.map((s, idx) => (
          <li key={`${s.at}-${idx}`} className="relative flex items-start gap-4 pl-1">
            <div className={cn('mt-0.5 size-4 shrink-0 rounded-full ring-2 ring-paper', idx === 0 ? 'bg-accent' : 'bg-rule')} />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <p className={cn('text-sm font-medium', idx === 0 ? 'text-ink' : 'text-inkSoft')}>{stepTitle(s)}</p>
                <p className="whitespace-nowrap text-xs tabular-nums text-muted">{stepTime(s.at)}</p>
              </div>
              {!isOrderedStep(s) && s.text && (
                <p className="mt-0.5 text-xs text-inkSoft">
                  <RemarkText text={s.text} />
                </p>
              )}
              {s.courier && (
                <p className="text-xs text-inkSoft">
                  Bưu tá {s.courier.name} ·{' '}
                  <a
                    href={`tel:${s.courier.phone}`}
                    className="-my-2.5 inline-flex min-h-11 items-center py-2.5 font-medium text-accent hover:underline desktop:my-0 desktop:min-h-0 desktop:py-0"
                  >
                    {phoneDisplay(s.courier.phone)}
                  </a>
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </>
  )
}
